import { useEffect, useState } from 'react'
import { contentAPI } from '../services/api'
import type { ContentExport, PostImageOptions } from '../types'
import { apiErrorMessage } from '../utils/apiError'
import Spinner from './Spinner'

// The size each network shows best; the creator can pick another.
const BEST_SIZE: Record<ContentExport['network'], PostImageOptions['sizes'][number]['id']> = {
  x: 'landscape',
  linkedin: 'landscape',
  instagram: 'portrait',
  tiktok: 'story',
  youtube: 'landscape',
}

/** A blob's text (FileReader where Blob.text is missing). */
const blobText = (blob: Blob): Promise<string> =>
  typeof blob.text === 'function'
    ? blob.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result))
        reader.onerror = () => reject(reader.error)
        reader.readAsText(blob)
      })

interface PostImagePickerProps {
  contentId: number
  network: ContentExport['network']
}

/**
 * An image to post with the text: a call card and chart for a post about a
 * call, a recap card for a recap, a quote card for any post. Drawn by
 * FinCreator from the post's own words and records, never generated, so it
 * can't show a number that isn't real.
 */
const PostImagePicker = ({ contentId, network }: PostImagePickerProps) => {
  const [options, setOptions] = useState<PostImageOptions | null>(null)
  const [kind, setKind] = useState<string | null>(null)
  const [size, setSize] = useState<string>(BEST_SIZE[network])
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let stopped = false
    // Optional: no picker when the options can't be loaded.
    Promise.resolve()
      .then(() => contentAPI.imageOptions(contentId))
      .then((o) => {
        if (stopped) return
        setOptions(o)
        setKind(o.kinds?.[0]?.id ?? null)
      })
      .catch(() => !stopped && setOptions({ kinds: [], sizes: [] }))
    return () => {
      stopped = true
    }
  }, [contentId])

  useEffect(() => {
    if (!kind) return
    let stopped = false
    let made: string | null = null
    setLoading(true)
    setError(null)
    contentAPI
      .image(contentId, kind, size)
      .then((blob) => {
        if (stopped) return
        made = URL.createObjectURL(blob)
        setUrl(made)
      })
      .catch(async (e) => {
        if (stopped) return
        setUrl(null)
        // The reason comes back as JSON inside a blob.
        const data = e?.response?.data
        let detail: string | null = null
        if (data instanceof Blob) {
          try {
            detail = JSON.parse(await blobText(data))?.detail ?? null
          } catch {
            detail = null
          }
        }
        setError(detail || apiErrorMessage(e, "Couldn't make this image."))
      })
      .finally(() => !stopped && setLoading(false))
    return () => {
      stopped = true
      if (made) URL.revokeObjectURL(made)
    }
  }, [contentId, kind, size])

  if (!options || options.kinds.length === 0) return null

  return (
    <div data-testid="post-image">
      <span className="text-sm font-medium text-gray-900">Image to post with it</span>
      <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Image">
        {options.kinds.map((k) => (
          <button
            key={k.id}
            type="button"
            aria-pressed={kind === k.id}
            onClick={() => setKind(k.id)}
            className={`rounded-full border px-3 py-1 text-xs font-medium ${
              kind === k.id ? 'border-primary-600 bg-primary-50 text-primary-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {k.label}
          </button>
        ))}
        <select
          aria-label="Image size"
          value={size}
          onChange={(e) => setSize(e.target.value)}
          className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700"
        >
          {options.sizes.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      <div className="mt-2 flex min-h-[8rem] items-center justify-center rounded-md bg-gray-100 p-2">
        {loading && <Spinner size="sm" />}
        {!loading && url && <img src={url} alt={options.kinds.find((k) => k.id === kind)?.label ?? ''} className="max-h-72 rounded" />}
        {!loading && error && (
          <p role="alert" className="text-sm text-danger-700">
            {error}
          </p>
        )}
      </div>
      {url && !loading && (
        <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
          <span>Made from your post and FinCreator's records, never generated.</span>
          <a
            href={url}
            download={`fincreator-${kind}-${size}.png`}
            className="rounded-md border border-gray-300 px-3 py-1 text-gray-700 hover:bg-gray-50"
          >
            Download image
          </a>
        </div>
      )}
    </div>
  )
}

export default PostImagePicker
